/**
 * What the player sees, keyed by the asset file GF04 renders. The agent gets these descriptions,
 * never the file names: Mission 0 names (package_low.svg, ...) encode the hidden reward tier,
 * while a description carries only what a human has - how the thing looks, including whatever
 * bias its look creates. Lookups return null for an unlisted file so callers fail closed instead
 * of forwarding a raw name.
 */

// Descriptions written from the rendered images in public/assets/images/packages.
export const MISSION0_PACKAGE_DESCRIPTIONS = {
  "package_low.svg": { item: "cloth_bundle", color: "light_blue", strap_color: null },
  "package_medium.svg": { item: "backpack", color: "peach", strap_color: "blue" },
  "package_high.svg": { item: "duffel_bag", color: "navy", strap_color: "green" },
};

// Mission 0's rule cue is a badge on the target car: a figure plus a large white letter.
// Descriptions written from the rendered images in public/assets/images/targetCars.
export const MISSION0_CUE_BADGES = {
  "target_precue_brightness.svg": { figure: "hooded_figure", letter: "B" },
  "target_precue_letter.svg": { figure: "hat_figure_with_crossed_arms", letter: "L" },
  "target_precue_number.svg": { figure: "figure_with_briefcase", letter: "N" },
};

// Mission 3's only cue is the target car itself (trialConfig.cueFile, same field as Mission 0's
// badge) - implicit, no on-screen text explains it. File names also carry a happy/neutral/sad
// expression, but it's always paired 1:1 with a fixed color, so it's dropped as redundant instead
// of handing the agent a pre-labeled emotional category. "target_blurred.svg" gets its own entry
// (not left unlisted) since the human genuinely can't see a color there either.
export const MISSION3_TARGET_CAR_DESCRIPTIONS = {
  "target_flat_blue_sad.svg": { shape: "flat", color: "blue" },
  "target_flat_green_neutral.svg": { shape: "flat", color: "green" },
  "target_flat_yellow_happy.svg": { shape: "flat", color: "yellow" },
  "target_round_blue_sad.svg": { shape: "round", color: "blue" },
  "target_round_green_neutral.svg": { shape: "round", color: "green" },
  "target_round_yellow_happy.svg": { shape: "round", color: "yellow" },
  "target_tall_blue_sad.svg": { shape: "tall", color: "blue" },
  "target_tall_green_neutral.svg": { shape: "tall", color: "green" },
  "target_tall_yellow_happy.svg": { shape: "tall", color: "yellow" },
  "target_blurred.svg": { shape: null, color: null },
};

// Mission 3 shows the star count on the package itself, so it is public (a range, not points).
// The bomb has no star count to show, so it's `stars: null` instead of a fake number.
export const MISSION3_PACKAGE_DESCRIPTIONS = {
  "package_1star.svg": { stars: 1, bomb: false },
  "package_2star.svg": { stars: 2, bomb: false },
  "package_3star.svg": { stars: 3, bomb: false },
  "escapePackage_1star.svg": { stars: 1, bomb: false },
  "escapePackage_2star.svg": { stars: 2, bomb: false },
  "escapePackage_3star.svg": { stars: 3, bomb: false },
  "escapePackage_bomb.svg": { stars: null, bomb: true },
};

// Vehicle type plus color - color alone would merge Frontbus_1 and Frontbus_2, which are both
// red. What each vehicle means is left to the instructions, as it is for the player.
export const FRONTBUS_DESCRIPTIONS = {
  "Frontbus_1.svg": { vehicle: "fire_truck", color: "red" },
  "Frontbus_2.svg": { vehicle: "ambulance", color: "red" },
  "Frontbus_3.svg": { vehicle: "school_bus", color: "yellow" },
  "Frontbus_4.svg": { vehicle: "bus", color: "teal" },
};

// The HQ contact's portrait shown beside its message - unlike the target car's expression word
// (dropped above because it doubled as a hidden rule cue), this one carries no rule information:
// it's just the contact's own visible reaction to what it's saying, same as a human reads it.
export const HQ_PORTRAIT_DESCRIPTIONS = {
  "hq-impressed.svg": "impressed_expression",
  "hq-happy.svg": "happy_expression",
  "hq-neutral.svg": "neutral_expression",
  "hq-speaking.svg": "speaking_expression",
};

// gs01_120px_<letter>_<letterLevel>_<digit>_<digitLevel>_<backgroundLevel>_set_NNNN[...].png - a
// letter and a digit drawn into binary pixel noise. Levels are the share of white pixels: letter
// and digit strokes in percent (10/90), background noise in tenths of a percent (450/550). The
// set number is only the noise seed, so it's left out. Image input isn't supported on the agent
// side yet, so the plate goes out as these visible properties - never a decoded category
// (vowel/odd/bright), which is the part the player has to work out.
const LICENSE_PLATE_FILE = /gs01_\d+px_([A-Za-z])_(\d+)_(\d+)_(\d+)_(\d+)_set_\d+/;

/** @returns {object | null} the license plate's visible properties, or null when the file doesn't parse */
export function describeLicensePlate(file) {
  const match = typeof file === "string" ? LICENSE_PLATE_FILE.exec(file.split("/").pop()) : null;
  if (!match) return null;
  const [, letter, letterLevel, digit, digitLevel, backgroundLevel] = match;
  return {
    letter: letter.toUpperCase(),
    digit,
    letter_brightness: Number(letterLevel) / 100,
    digit_brightness: Number(digitLevel) / 100,
    background_brightness: Number(backgroundLevel) / 1000,
  };
}

/** @returns {string | null} the description for a rendered file, or null when unlisted */
export function describeCue(descriptions, file) {
  if (!file) return null;
  return Object.prototype.hasOwnProperty.call(descriptions, file) ? descriptions[file] : null;
}
